import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, ChevronsDownUp, X, Magnet } from "lucide-react";
import { toast } from "sonner";
import { useFabsCollapsed, setFabsCollapsed } from "../lib/fabTidy";

// Mobile-only button (bottom-left) that collapses/expands ALL floating helpers.
// Like every other floating helper, it can also be tucked off-screen with its
// own little X, leaving a small peek tab on the left edge to bring it back.
export default function FabTidyToggle() {
  const location = useLocation();
  const collapsed = useFabsCollapsed();
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem("iw_tidy_hidden") === "1"; } catch { return false; }
  });
  // The green magnet can be tucked away with its own X (like the other helpers).
  // It comes back the next time the helpers are expanded via "Show Helpers".
  const [magnetHidden, setMagnetHidden] = useState(false);
  const hide = () => { try { localStorage.setItem("iw_tidy_hidden", "1"); } catch {} setHidden(true); };
  const show = () => { try { localStorage.removeItem("iw_tidy_hidden"); } catch {} setHidden(false); };
  // Snap every floating helper back into its default, tidy stack (undo any
  // dragging). Also makes sure they're shown so the result is visible.
  const tidyUp = () => { setFabsCollapsed(false); try { window.dispatchEvent(new Event("iw-fabs-tidy")); } catch { /* noop */ } try { toast.success("Everything's back! ✨", { duration: 1800 }); } catch { /* noop */ } };

  // On the Professor Owl page the bottom is taken by the chat input + camera/
  // attach controls, so the tidy toggle sits at the LEFT edge, vertically
  // centered, instead of bottom-left. Elsewhere it stays bottom-left.
  const onTutor = location.pathname.startsWith("/learn/tutor");
  const restorePos = onTutor ? "top-1/2 -translate-y-1/2 left-0" : "bottom-[88px] left-0";
  const dockPos = onTutor ? "top-1/2 -translate-y-1/2 left-3" : "bottom-[88px] left-3";

  // Tucked away: only a small peek tab on the left edge to bring it back.
  if (hidden) {
    return (
      <button
        type="button"
        onClick={show}
        className={`fixed ${restorePos} z-[70] flex items-center justify-center active:scale-95 transition-transform isaiah-peek-wiggle`}
        style={{
          width: 26, height: 44,
          borderTopRightRadius: 9999, borderBottomRightRadius: 9999,
          border: "1px solid rgba(255,255,255,0.2)", borderLeft: "none",
          background: "rgba(15,23,42,0.92)", boxShadow: "0 4px 14px rgba(0,0,0,0.4)",
        }}
        data-testid="fab-tidy-restore"
        aria-label="Show helper controls"
        title="Show helper controls"
      >
        <Sparkles size={15} className="text-amber-300" />
      </button>
    );
  }

  return (
    <div className={`fixed ${dockPos} z-[70]`}>
      <div className="flex flex-col items-center gap-2.5">
        {/* The magnet appears/disappears WITH the other helpers — only shown
            when they're expanded (tapping Show Helpers hides it too). It also
            has its own X to tuck it away; it returns when helpers are expanded. */}
        {!collapsed && !magnetHidden && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setMagnetHidden(true)}
              className="absolute -top-1 -right-1 z-10 w-4 h-4 rounded-full bg-slate-900 border border-white/60 flex items-center justify-center shadow hover:bg-black active:scale-95 transition-all"
              data-testid="fab-tidy-snap-hide"
              aria-label="Hide the snap-back magnet"
              title="Hide the magnet"
            >
              <X size={10} className="text-white/90" />
            </button>
            <button
              type="button"
              onClick={tidyUp}
              className="w-11 h-11 rounded-full flex items-center justify-center shadow-lg border border-emerald-300/30 bg-emerald-600/90 backdrop-blur text-white active:scale-95 transition-transform"
              data-testid="fab-tidy-snap"
              aria-label="Tidy helper icons into a neat stack"
              title="Tidy the gift, flag &amp; command icons back into a neat stack"
            >
              <Magnet size={20} className="text-white" />
            </button>
          </div>
        )}
        {/* The toggle + its X live together so the X stays pinned to the button
            and is ALWAYS visible, whether helpers are shown or hidden. */}
        <div className="relative">
          <button
            type="button"
            onClick={hide}
            className="absolute -top-1 -right-1 z-10 w-4 h-4 rounded-full bg-slate-900 border border-white/60 flex items-center justify-center shadow hover:bg-black active:scale-95 transition-all"
            data-testid="fab-tidy-hide"
            aria-label="Hide helper controls"
            title="Hide (tap the edge tab to bring it back)"
          >
            <X size={10} className="text-white/90" />
          </button>
          <button
            type="button"
            onClick={() => { if (collapsed) setMagnetHidden(false); setFabsCollapsed(!collapsed); }}
            className="w-11 h-11 rounded-full flex items-center justify-center shadow-lg border border-white/20 bg-slate-900/90 backdrop-blur text-white/80 active:scale-95 transition-transform"
            data-testid="fab-tidy-toggle"
            aria-label={collapsed ? "Show helpers" : "Hide helpers"}
            title={collapsed ? "Show helpers" : "Hide helpers"}
          >
            {collapsed ? <Sparkles size={20} className="text-amber-300" /> : <ChevronsDownUp size={20} />}
          </button>
        </div>
      </div>
    </div>
  );
}
